import { showNotification } from '../LanghoutBetonPortal';

import {
  DOMAIN,
  JWT_KEY_LOCALSTORAGE,
  JWT_OTP_KEY_LOCALSTORAGE,
} from '../environment/environment';

const version = 'v0.0.1';
export const currentVersion = version;
export const baseURL = `https://${DOMAIN}`;

const genericErrorMessage = 'Er is iets misgegaan. Probeer het opnieuw.';
const connectivityErrorMessage =
  'Controleer uw internetverbinding en probeer het opnieuw.';

export type APIProblem = {
  detail: string;
  type?: string;
  title?: string;
  status?: number;
  instance?: string;
  [extension: string]: unknown;
};

export type APIResponse = {
  body?: unknown;
  headers: Headers;
  statusCode?: number;
  error_message?: string;
  problem?: APIProblem;
  transportError?: true;
};

export type SuccessfulAPIResponse = APIResponse & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any;
  statusCode: number;
  problem?: undefined;
};

export function isSuccessfulAPIResponse(
  response: APIResponse,
): response is SuccessfulAPIResponse {
  return (
    response.statusCode !== undefined &&
    response.statusCode >= 200 &&
    response.statusCode < 300 &&
    response.body !== undefined &&
    response.problem === undefined
  );
}

export async function unAuthenticatedAPICall(
  path: string,
  method: string,
  headers: Headers,
  body?: BodyInit | null,
): Promise<APIResponse> {
  return apiCall(path, method, headers, body);
}

export async function authenticatedAPICall(
  path: string,
  method: string,
  headers: Headers,
  body?: BodyInit | null,
  key?: string | null,
): Promise<APIResponse> {
  let jwtKey = JWT_KEY_LOCALSTORAGE;
  if (key) {
    jwtKey = key;
  }
  const jwtFromStorage = localStorage.getItem(jwtKey);
  if (!jwtFromStorage) {
    return { headers: new Headers() };
  }
  headers.set('Authorization', 'Bearer ' + jwtFromStorage);
  return apiCall(path, method, headers, body);
}

export async function otpCall(
  path: string,
  method: string,
  headers: Headers,
  body?: BodyInit | null,
): Promise<APIResponse> {
  const jwtFromStorage = localStorage.getItem(JWT_OTP_KEY_LOCALSTORAGE);
  if (!jwtFromStorage) {
    return { headers: new Headers() };
  }
  headers.set('Authorization', 'Bearer ' + jwtFromStorage);
  return apiCall(path, method, headers, body);
}

async function apiCall(
  path: string,
  method: string,
  headers: Headers,
  body?: BodyInit | null,
): Promise<APIResponse> {
  const requestInit: RequestInit = {
    method: method,
    mode: 'cors',
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    headers: defaultHeaders(headers),
  };
  if (body !== undefined && body !== null) {
    requestInit.body = body;
  }

  let response: Response;
  try {
    response = await fetch(baseURL + path, requestInit);
  } catch (error) {
    console.error('Unable to execute request', error);
    showNotification(connectivityErrorMessage);
    return { headers: new Headers(), transportError: true };
  }

  return handleResponse(response);
}

async function handleResponse(response: Response): Promise<APIResponse> {
  const responseDetails: APIResponse = {
    headers: response.headers,
    statusCode: response.status,
  };

  let responseText: string;
  try {
    responseText = await response.text();
  } catch (error) {
    console.error('Unable to read API response', error);
    return responseError(responseDetails);
  }

  if (responseText.trim() === '') {
    return response.ok ? responseDetails : responseError(responseDetails);
  }

  let responseBody: unknown;
  try {
    responseBody = JSON.parse(responseText);
  } catch (error) {
    console.error('Unable to parse API response', error);
    return responseError(responseDetails);
  }

  if (response.ok) {
    return { ...responseDetails, body: responseBody };
  }

  const problem = parseProblem(response, responseBody);
  if (!problem) {
    return responseError(responseDetails);
  }

  showNotification(problem.detail);
  return {
    ...responseDetails,
    body: problem,
    error_message: problem.detail,
    problem,
  };
}

function responseError(responseDetails: APIResponse): APIResponse {
  showNotification(genericErrorMessage);
  return {
    ...responseDetails,
    error_message: genericErrorMessage,
  };
}

function parseProblem(
  response: Response,
  responseBody: unknown,
): APIProblem | undefined {
  const contentType = response.headers.get('Content-Type') ?? '';
  const mediaType = contentType.split(';', 1)[0].trim().toLowerCase();
  if (mediaType !== 'application/problem+json' || !isRecord(responseBody)) {
    return undefined;
  }

  if (
    typeof responseBody.detail !== 'string' ||
    responseBody.detail.trim() === '' ||
    (responseBody.type !== undefined &&
      typeof responseBody.type !== 'string') ||
    (responseBody.title !== undefined &&
      typeof responseBody.title !== 'string') ||
    (responseBody.status !== undefined &&
      (typeof responseBody.status !== 'number' ||
        !Number.isInteger(responseBody.status) ||
        responseBody.status < 100 ||
        responseBody.status > 599)) ||
    (responseBody.instance !== undefined &&
      typeof responseBody.instance !== 'string')
  ) {
    return undefined;
  }

  return responseBody as APIProblem;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function defaultHeaders(headers: Headers): Headers {
  headers.set('Content-Type', 'application/json');
  headers.set('X-VERSION', version);
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', 'nl');
  }
  return headers;
}
